import type { Region } from '~~/core/entities/Region';

import { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import { getCleanStaticListingPath } from '../links/cleanStaticListingPath';
import { getRegionMetadata } from './utils';

const regionName = 'Pinheiros';

const metaTags = getRegionMetadata(regionName, 'São Paulo', {
	gender: 'neutral'
});

const region: Region = {
	name: regionName,
	state: 'sp',
	city: 'São Paulo',
	country: 'Brasil',
	path: '/bairros/pinheiros',
	image: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/banner.webp',
	metaTags,
	info: {
		title: '',
		description:
			'São Paulo é uma cidade vibrante e diversificada, conhecida por sua infraestrutura moderna, opções culturais ricas e oportunidades de negócios incomparáveis. Morar em São Paulo significa estar próximo de tudo: excelentes restaurantes, shoppings de alto padrão, parques, centros culturais e muito mais. Bairros como Jardins, Moema e Itaim Bibi oferecem uma qualidade de vida excepcional com suas ruas arborizadas e serviços de alto nível.</br></br>Optar pela Pilar para encontrar seu imóvel ideal em São Paulo garante acesso a uma seleção exclusiva de propriedades de alto padrão. Nossa equipe de corretores qualificados está pronta para oferecer suporte personalizado, desde a busca do imóvel perfeito até a finalização da compra. Com a Pilar, você encontra segurança, confiança e a certeza de estar fazendo o melhor investimento. Descubra o seu próximo lar em São Paulo com a Pilar.'
	},
	hero: {
		title: regionName,
		description:
			'Momentos de lazer, alta gastronomia e ótimos drinks estão no bairro mais procurado de São Paulo.',
		backgroundImage:
			'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/banner.webp'
	},
	map: {
		title: `${regionName}: viva a energia do bairro mais badalado da cidade`,
		content: `Procura uma localização incrível, ótimas opções de transporte público e ainda quer fazer tudo que tem direito sem sair do bairro? Pinheiros é a opção ideal. Seguindo toda a extensão da Avenida Rebouças, Pinheiros é uma unanimidade entre os moradores: quem mora lá, não quer sair. <br/><br/>A mistura entre ruas arborizadas, ótimas opções de gastronomia, bares, baladas, mercados, shoppings, cinemas de rua e até a Faria Lima transformam Pinheiros em um dos bairros mais cobiçados de São Paulo, seja por jovens adultos que procuram um lugar para viver na boemia, até famílias, que encontram ruas tranquilas no bairro.<br><br>Se de dia o bairro se torna mais tranquilo, à noite, Pinheiros se transforma em um bairro boêmio com ótimas opções de bares e restaurantes, seja para um happy hour, uma noite a dois ou até sair com as crianças para jantar. <br><br> Aos fins de semana, Pinheiros abriga diversos visitantes que procuram opções de lazer. Os badalados restaurantes abrem o dia inteiro, o que torna mais fácil a decisão de onde fazer o almoço especial, na rua Fradique Coutinho, o CineSala, um dos poucos cinemas de rua da cidade, forma fila na calçada, graças à sua programação eclética. A seguir, apresentamos o que torna este bairro no coração da zona oeste de São Paulo um dos mais especiais para se viver.`,
		image: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/map.webp',
		links: [
			{
				label: 'Ver imóveis em Pinheiros',
				url: getCleanStaticListingPath({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Restaurantes em ${regionName}`,
				content:
					'Ama comer bem? Não faltam opções em Pinheiros! Na rua dos Pinheiros, a Tasca do Zé e da Maria serve uma das culinárias portuguesas mais prestigiadas de São Paulo. Seu ambiente com grandes janelas é perfeito para ir durante o dia. À noite, o ambiente se transforma em um local intimista e acolhedor. Na mesma rua, o ELLA | FITZ, dos criadores do Modern Mamma Osteria, apresenta uma gastronomia italiana para além das massas, com carnes e frutos do mar saborosos. E falando em frutos do mar, a esquina que abriga o Atlântico 202, mal abriu e já se tornou um dos restaurantes mais queridos do bairro, de Victor Collor de Melo, nome por trás do Cozinha 212, o restaurante serve peixes e outros frutos do mar na brasa. Um destaque para o bar de ostras, que funciona o dia todo e serve, além de ostras fresquíssimas, mariscos, vieiras e outros mexilhões.<br><br>Falando em comida asiática, o Jun Sakamoto, eleito diversas vezes o melhor restaurante japonês da cidade e o primeiro a receber uma estrela Michelin, está localizado em Pinheiros há mais de 20 anos. O Omakase no balcão, com o sushiman preparando os pratos e os servindo, é imperdível. Listado em 2024 no Bib Gourmand da Michelin, o Kotori apresenta uma comida de inspiração japonesa quente e saborosa. Seu irmão mais velho, o Tan Tan, é o único brasileiro na lista 50 Best dos 50 melhores bares do mundo.'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-1.webp',
					credits: 'VK bro'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-2.webp',
					credits: 'Jason Leung',
					class: 'col-start-2'
				}
			]
		},
		{
			info: {
				title: `Os clubes de ${regionName}`,
				content:
					'No Jardim Paulistano não faltam opções de lazer ao ar livre. Rodeado de árvores e com boas praças, com certeza você vai encontrar um programa para fazer aos fins de semana, mas se você procura um local para praticar esportes, curtir uma piscina e até conhecer gente nova, dois clubes estão localizados muito próximos ao bairro.<br><br>Um dos mais tradicionais de São Paulo, o Esporte Clube Pinheiros está localizado na Av. Faria Lima, ao lado do Shopping Iguatemi. É conhecido por sua vasta gama de esportes e forma alguns dos principais atletas do país.<br><br>Já o Clube Hebraica reúne 22 mil sócios e conta com escola maternal e infantil, além de uma série de atividades voltadas para empreendedores, de co-working à cursos profissionalizantes. Opções de esportes e lazer também não faltam.'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-3.webp'
				}
			]
		},
		{
			info: {
				title: `Parques em ${regionName}`,
				content:
					'A Praça Benedito Calixto é figurinha carimbada para os moradores de Pinheiros. Arborizada, a praça abriga feira livre todas as terças. Aos sábados, a feirinha de antiguidades tradicional do bairro deixa o local, que conta com ótimos cafés e restaurantes em seu entorno, lotada de moradores e visitantes. O playground, queridinho das crianças, fica aberto todos os dias.<br><br>Se você procura um lugar para ir com seu pet, o ParCão Pinheiros, localizado entre as ruas Mateus Grou e Doutor Virgílio de Carvalho Pinto é uma boa pedida. Além de área fechada para soltar os pets, a praça ainda conta com um playground para crianças.<br><br>Apesar de não tão próximo, o Parque Villa Lobos é de fácil acesso para os moradores de Pinheiros, graças à ciclovia da Rua Pedroso de Morais. Pegue sua bike e pedale até o parque para um dia de diversão com a família. Aos domingos, a ciclofaixa se estende até o parque, sendo uma ótima opção para quem quer fazer um programa diferente.'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-4.webp'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-5.webp'
				}
			]
		},
		{
			info: {
				title: `Imóveis em ${regionName}`,
				content:
					'Misturando novos prédios com construções icônicas em São Paulo, o bairro de Pinheiros é a união perfeita entre a tradição e o moderno. Na rua Simão Álvares, o destaque vai para os predinhos de três andares conhecidos como “predinhos da hípica”. Construídos pelo cafeicultor libanês Raduan Dabus nos anos 50 no antigo terreno da Sociedade Hípica Paulista, os edifícios são patrimônio histórico da cidade.<br><br>Entre as novidades, o condomínio Joaquim 499 é eleito por muitos o prédio mais bonito do bairro. De altíssimo padrão, o edifício conta com apartamentos de 87 m² até 179 m², lazer completo e uma das padarias mais charmosas de Pinheiros no térreo, a Le Jazz Boulangerie.',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-6.webp',
					credits: 'IdeiaZarvos',
					class: 'row-start-2'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/content-7.webp',
					class: 'row-start-2'
				}
			]
		}
	],
	nearRegions: [
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulista/banner.webp',
			name: 'Jardim Paulista',
			href: '/bairros/jardim-paulista'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/vila-nova-conceicao/banner.webp',
			name: 'Vila Nova Conceição',
			href: '/bairros/vila-nova-conceicao'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/alto-de-pinheiros/banner.webp',
			name: 'Alto de Pinheiros',
			href: '/bairros/alto-de-pinheiros'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-europa/banner.webp',
			name: 'Jardim Europa',
			href: '/bairros/jardim-europa'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/banner.webp',
			name: 'Jardim Paulistano',
			href: '/bairros/jardim-paulistano'
		}
	],
	questions: [
		{
			question: `Onde fica ${regionName}?`,
			answer:
				'Pinheiros está localizado na cidade de São Paulo, na zona Oeste, no distrito de Pinheiros, sob a subprefeitura de mesmo nome. O bairro faz parte de uma região estratégica e de fácil acesso, com proximidade de bairros como Vila Madalena, Jardim Paulistano e Jardim Europa. As principais avenidas que conectam o bairro a outras regiões da cidade são a Avenida Faria Lima, Avenida Rebouças e Avenida Pedroso de Morais, facilitando o tráfego e o deslocamento para diferentes partes da cidade.'
		},
		{
			question: `Como chegar em ${regionName}?`,
			answer:
				'Pinheiros é bem servido por transporte privado e público. Para quem viaja de carro, o bairro é facilmente acessado por importantes avenidas como a Avenida Faria Lima, Avenida Rebouças e Avenida Pedroso de Morais, além da proximidade com a Marginal Pinheiros, que facilita o deslocamento para outras regiões da cidade. O bairro também conta com diversas linhas de ônibus e está próximo à Estação Faria Lima da Linha 4-Amarela do Metrô. Além disso, Pinheiros possui boas ciclovias, proporcionando fácil acesso à ciclovia da Marginal Pinheiros. Para quem prefere o transporte privado, o acesso ao bairro é facilitado pelas principais avenidas e pela Marginal, garantindo uma locomoção rápida e conveniente.'
		},
		{
			question: `O que fazer em ${regionName}?`,
			answer:
				'Pinheiros é um bairro vibrante, com diversas opções de lazer que atendem a todos os gostos. Durante o dia, a Praça Benedito Calixto é um destaque, com sua feira livre durante a semana e a tradicional feira de antiguidades aos sábados. O Esporte Clube Pinheiros é um destaque para quem ama esportes, oferecendo infraestrutura completa com quadras de tênis, piscinas e diversas opções de lazer. O Parque Villa-Lobos, com seus amplos espaços para caminhadas, atividades físicas e piqueniques, é outra excelente opção para quem deseja aproveitar o ar livre. Para os amantes de cinema, o CineSala oferece uma programação variada em um ambiente acolhedor, ideal para um programa mais intimista. A Rua dos Pinheiros também é um excelente destino para quem gosta de compras e cultura, com uma grande variedade de lojas de design, roupas, acessórios e galerias de arte.'
		},
		{
			question: `Quais são os restaurantes em ${regionName}?`,
			answer:
				'Pinheiros oferece uma gastronomia de alta qualidade e diversidade. Se você adora comida portuguesa, a Tasca do Zé e da Maria é uma ótima pedida. Para um jantar sofisticado, o ELLA | FITZ serve pratos italianos modernos, enquanto o Atlântico 202 oferece frutos do mar frescos e uma excelente seleção de ostras. Já o Jun Sakamoto é uma referência na culinária japonesa e tem o reconhecimento da estrela Michelin. Outro destaque é o Kotori, que traz uma proposta de cozinha japonesa quente e inovadora. Para um ambiente mais descontraído, o bar de ostras no Atlântico 202 é imperdível.'
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Apartamento',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Cobertura',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Studio',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.STUDIO],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Duplex',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.DUPLEX],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Garden',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.GARDEN],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Jardim Paulista, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					},
					{
						label: 'Jardim América, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim América']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'Pinheiros é um dos bairros mais desejados de São Paulo, combinando excelente gastronomia, espaços verdes e opções de lazer sofisticadas. Os restaurantes são uma atração à parte, com locais renomados como a Tasca do Zé e da Maria, que oferece uma autêntica culinária portuguesa, e o Jun Sakamoto, aclamado por sua gastronomia japonesa de alta qualidade. À noite, o bairro ganha vida com opções perfeitas para jantares elegantes e happy hours descontraídos.',
				`O Parque Villa-Lobos se destaca com suas áreas verdes, ciclovias e programação cultural, enquanto o ParCão Pinheiros é ideal para amantes de pets, proporcionando um espaço requintado. Para lazer, clubes como o Esporte Clube Pinheiros e o Clube Hebraica oferecem infraestrutura completa para esportes e atividades sociais. Em termos de imóveis, Pinheiros apresenta uma variedade de opções, com opções que vão de charmosas construções históricas, como os icônicos  "predinhos da Hípica" na rua Simão Álvares, que são patrimônios históricos da cidade, até modernos condomínios de alto padrão, como o Joaquim 499, exemplo de sofisticação, com apartamentos de alta qualidade e uma padaria gourmet no térreo, atraindo aqueles que buscam conforto e estilo de vida elevado.`
			]
		}
	}
};

export default region;
